import { useContext } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { FEE_TYPE_LABELS } from '@/components/PriceWithDetails/usePriceDetailsRows';
import Stack from '@/design_system/Stack';
import { Body, Cell, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import { AgreementFromGetById } from '@/models/agreements';
import { useDatabaseTranslation } from '@/services/i18n';

import { AgreementEditContext } from '../AgreementEditContext';

import {
  getPriceAgreementColumnWidths,
  PriceAgreementTableHeader,
  PriceAgreementTableRow,
  PriceAgreementTableRowEdit,
  PriceAgreementTableRowNew,
} from './PriceAgreementTableRow';

export const FeesTable = ({ fees }: { fees: AgreementFromGetById['prices'] }) => {
  const { _compare } = useDatabaseTranslation();
  const { draft } = useContext(AgreementEditContext);

  const sorted = fees.sort((a, b) => _compare(a.feeType!.name!, b.feeType!.name!));

  if (draft) {
    return <FeesTableEdit fees={fees} />;
  }

  return (
    <Table columnWidths={getPriceAgreementColumnWidths({})} bordered>
      <PriceAgreementTableHeader />
      <Body>
        {sorted.map((price) => (
          <PriceAgreementTableRow key={price.id} price={price}>
            <FeeNameCell price={price} />
          </PriceAgreementTableRow>
        ))}
        {sorted.length === 0 && (
          <Row>
            <Cell isWholeRow>
              <Paragraph color="secondary">
                <Trans id="settings.organization.entities.agreements.detail.empty.fees">
                  No fee prices on this agreement.
                </Trans>
              </Paragraph>
            </Cell>
          </Row>
        )}
      </Body>
    </Table>
  );
};

const FeesTableEdit = ({ fees }: { fees: AgreementFromGetById['prices'] }) => {
  const { t, i18n } = useLingui();
  const { draft } = useContext(AgreementEditContext);

  const missingFeeTypes = Object.entries(FEE_TYPE_LABELS).filter(
    ([slug]) => !draft.prices.some((price) => price.feeType?.slug === slug)
  );

  return (
    <Table columnWidths={getPriceAgreementColumnWidths({ withActions: true })} bordered>
      <PriceAgreementTableHeader withActions />
      <Body>
        {fees.map((price) => (
          <PriceAgreementTableRowEdit key={price.id} price={price}>
            <FeeNameCell price={price} />
          </PriceAgreementTableRowEdit>
        ))}
        <PriceAgreementTableRowNew
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.table.fees.add',
            message: 'Add fee',
          })}
          options={missingFeeTypes.map(([id, label]) => ({
            label: i18n._(label),
            id,
          }))}
          onSelect={(value) => ({
            feeType: {
              id: value.id,
              slug: value.id as any,
              name: {
                en: i18n._(value.label),
                fr: i18n._(value.label),
                ja: i18n._(value.label),
              },
            },
          })}
        />
      </Body>
    </Table>
  );
};

const FeeNameCell = ({ price }: { price: AgreementFromGetById['prices'][number] }) => {
  const { _db } = useDatabaseTranslation();

  return (
    <Stack row gap="0.35rem" alignItems="center" flexWrap="nowrap">
      <IconAdd style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
      <Paragraph>{_db(price.feeType!.name!)}</Paragraph>
    </Stack>
  );
};
